import { DateInputField, Input } from '@ui'
import { medicationDurationDays } from '@util'
import { useEffect, useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

const DAY_MS = 24 * 60 * 60 * 1000

const toMs = (value: unknown): number | null => {
  if (!value) return null
  const ms = new Date(value as string | Date).getTime()
  return Number.isNaN(ms) ? null : ms
}

/**
 * Start, duration (days) and end of a medication order, kept in sync:
 * typing a duration sets the end date, picking an end date updates the duration,
 * and moving the start date shifts the end date so the duration is preserved.
 */
export const MedicationDateRange = () => {
  const { control, setValue } = useFormContext()
  const startAt = useWatch({ control, name: 'start_at' })
  const endAt = useWatch({ control, name: 'end_at' })
  const [duration, setDuration] = useState(() => String(medicationDurationDays(startAt, endAt) ?? ''))
  const prev = useRef({ end: toMs(endAt), start: toMs(startAt) })

  useEffect(() => {
    const start = toMs(startAt)
    const end = toMs(endAt)
    const last = prev.current

    // Only the start moved (not a form reset): carry the end date along with it.
    if (start != null && start !== last.start && end === last.end && last.start != null && last.end != null) {
      const shifted = new Date(start + (last.end - last.start)).toISOString()
      prev.current = { end: toMs(shifted), start }
      setValue('end_at', shifted, { shouldDirty: true })
      return
    }

    prev.current = { end, start }
    const days = medicationDurationDays(startAt, endAt)
    setDuration(days == null ? '' : String(days))
  }, [startAt, endAt, setValue])

  const onDurationChange = (value: string) => {
    setDuration(value)
    const days = Number(value)
    const start = toMs(startAt)
    if (value === '' || !Number.isInteger(days) || days < 0 || start == null) return
    setValue('end_at', new Date(start + days * DAY_MS).toISOString(), { shouldDirty: true })
  }

  return (
    <>
      <DateInputField dateFormat="MMMM d, yyyy HH:mm" label="Start Date and Time" name="start_at" showTimeSelect />
      <div>
        <p className="mb-[2px] text-sm font-medium">Duration (days)</p>
        <Input
          min={0}
          name="duration_days"
          onChange={e => onDurationChange(e.target.value)}
          placeholder="e.g. 5"
          step={1}
          type="number"
          value={duration}
        />
      </div>
      <DateInputField dateFormat="MMMM d, yyyy HH:mm" label="End Date and Time" name="end_at" showTimeSelect />
    </>
  )
}
